import api from './apiConfig';
import { validateArrayBufferSize } from '../utils/arrayBufferUtils';

// Codex service with real API calls
const codexService = {
  getAll: async (params = {}) => {
    try {
      // Ensure pagination parameters are properly formatted
      const queryParams = {
        page: params.page || 0,
        size: params.size || 10,
        sort: params.sort || 'createdAt,desc',
        ...params
      };

      const response = await api.get('/codexes', { params: queryParams });
      return response.data;
    } catch (error) {
      console.error('Error fetching codexes:', error);
      throw error;
    }
  },

  getById: async (id) => {
    try {
      const response = await api.get(`/codexes/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching codex ${id}:`, error);
      throw error;
    }
  },

  create: async (codexData, file) => {
    try {
      const formData = new FormData();
      formData.append('codex', JSON.stringify(codexData));
      if (file) {
        formData.append('file', file);
      }

      const response = await api.post('/codexes', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error creating codex:', error);
      throw error;
    }
  },

  update: async (id, data) => {
    try {
      const response = await api.put(`/codexes/${id}/attributes`, data);
      return response.data;
    } catch (error) {
      console.error(`Error updating codex ${id}:`, error);
      throw error;
    }
  },

  delete: async (id) => {
    try {
      const response = await api.delete(`/codexes/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting codex ${id}:`, error);
      throw error;
    }
  },

  getByType: async (codexTypeId) => {
    try {
      const response = await api.get(`/codexes/by-type/${codexTypeId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching codexes for type ${codexTypeId}:`, error);
      throw error;
    }
  },

  deleteFile: async (fileId) => {
    try {
      const response = await api.delete(`/codex-files/${fileId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting file ${fileId}:`, error);
      throw error;
    }
  },

  addFile: async (fileData, codexId) => {
    try {
      const formData = new FormData();

      formData.append('file', fileData.file);
      const response = await api.post(`/codex-files?codexId=${codexId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error adding file:', error);
      throw error;
    }
  },

  // Get file content
  getFileContent: async (fileId) => {
    try {
      const response = await api.get(`/codex-files/${fileId}/content`, {
        responseType: 'arraybuffer'  // Use arraybuffer for binary data
      });

      // Validate ArrayBuffer size for reasonable browser limits
      const validation = validateArrayBufferSize(response.data, 200); // 200MB limit
      if (!validation.canProceed) {
        throw new Error(
          `File too large to process: ${validation.sizeMB}MB. ` +
          `Browser memory limit exceeded. ${validation.recommendation}`
        );
      }

      // Return ArrayBuffer directly - no conversion needed!
      // Blob URLs handle binary data natively without memory overhead
      return response.data;
    } catch (error) {
      console.error(`Error fetching file content for file ${fileId}:`, error);
      throw error;
    }
  },

  // Create a new codex with process
  createCodexWithProcess: async (formData) => {
    try {
      // Utilizziamo headers multipart/form-data per l'upload dei file
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      };

      const response = await api.post('/codexes/with-process', formData, config);
      return response.data;
    } catch (error) {
      console.error('Error creating codex with process:', error);
      throw error;
    }
  },

  getThumbnail: async (id, updatedAt) => {
    try {
      // Always use current timestamp to force reload
      const timestamp = Date.now();
      const response = await api.get(`/codexes/${id}/thumbnail?v=${timestamp}`);
      return response.data;
    } catch (error) {
      // Thumbnail might not exist (404) - return null silently
      if (error.response && error.response.status === 404) {
        return null;
      }
      // Only log non-404 errors
      console.error(`Error loading thumbnail for codex ${id}:`, error);
      throw error;
    }
  }

};

export default codexService;
